import moment from 'moment';
import polyline from 'polyline';
import _ from 'underscore';

import AgencyUser from 'common/types/AgencyUser';
import RideSchedule from 'common/types/RideSchedule';
import {serviceTypes} from 'common/types/Service';
import {naturalSort, omitNull, positionBounds} from 'common/utils';


window.polyline = polyline;


export const rideStatus = {
  NEEDS_APPROVAL: 'needs_approval',
  DENIED: 'denied',
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  CANCELED: 'canceled',
  NO_SHOW: 'no_show',
};

export const readableRideStatus = {
  [rideStatus.NEEDS_APPROVAL]: 'Needs Approval',
  [rideStatus.DENIED]: 'Denied',
  [rideStatus.PENDING]: 'Pending',
  [rideStatus.IN_PROGRESS]: 'In Progress',
  [rideStatus.COMPLETE]: 'Completed',
  [rideStatus.CANCELED]: 'Canceled',
  [rideStatus.NO_SHOW]: 'No Show',
};

export const messageKey = {
  VEHICLE_EN_ROUTE: 'vehicle_en_route',
  RIDE_DENIAL: 'ride_denial',
  RIDER_NO_SHOW: 'rider_no_show',
  CANCELED_BY_USER: 'canceled_by_user',
  CANCELED_BY_DISPATCHER: 'canceled_by_dispatcher',
  CANCELED_BY_SYSTEM: 'canceled_by_system',
};

// Separate set of event names used for new nomenclature in Dispatcher 2.0
// TODO: (bshiver) Update the rest of the app to consistently use this terminology
export const readableRideEvents = Object.assign({}, readableRideStatus, {
  [rideStatus.PENDING]: 'Booked',
  [rideStatus.IN_PROGRESS]: 'Picked Up',
});

export const rideSource = {
  RIDER: 'rider',
  DISPATCHER: 'dispatcher',
  DRIVER: 'driver',
};

const rideSchedule = ride => ride && ride.schedule;

export const isNextPickup = ride =>
  ride &&
  ride.serviceType === serviceTypes.ONDEMAND &&  // exclude traditional in-advance
  ride.status === rideStatus.PENDING &&
  rideSchedule(ride) &&
  ride.schedule.pickup &&
  ride.schedule.pickup.isNext;

export const isNextDropoff = ride =>
  ride &&
  ride.status === rideStatus.IN_PROGRESS &&
  rideSchedule(ride) &&
  ride.schedule.dropoff &&
  ride.schedule.dropoff.isNext;

export const isNextStop = ride => {
  if (!ride) {
    return false;
  } else if (ride.status === rideStatus.PENDING) {
    return isNextPickup(ride);
  } else if (ride.status === rideStatus.IN_PROGRESS) {
    return isNextDropoff(ride);
  }
  return false;
};

export const interimPickupStopCount = ride =>
  ride &&
  ride.schedule &&
  ride.schedule.pickup &&
  ride.schedule.pickup.interim_stop_count;

export const interimDropoffStopCount = ride =>
  ride &&
  ride.schedule &&
  ride.schedule.dropoff &&
  ride.schedule.dropoff.interim_stop_count;

export const interimStopCount = ride => {
  if (!ride) {
    return null;
  } else if (ride.status === rideStatus.PENDING) {
    return interimPickupStopCount(ride);
  } else if (ride.status === rideStatus.IN_PROGRESS) {
    return interimDropoffStopCount(ride);
  }
  return null;
};

export const pickupEstimate = ride =>
  rideSchedule(ride) &&
  ride.schedule.pickup &&
  ride.schedule.pickup.timestamp;

export const dropoffEstimate = ride =>
  rideSchedule(ride) &&
  ride.schedule.dropoff &&
  ride.schedule.dropoff.timestamp;

export const rideVehicleId = ride =>
  rideSchedule(ride) &&
  ride.schedule.vehicle &&
  ride.schedule.vehicle.vehicleId;

const parsePosition = position =>
  (position ? {lat: position.latitude, lng: position.longitude} : null);

const serializePosition = position =>
  (position ? {latitude: position.lat, longitude: position.lng} : null);

export const NOTE_LENGTH = 80;  // mapped to varchar(80) in the db

export default class Ride {
  /* eslint-disable camelcase */
  constructor({
    agency,
    terminal_reason,
    caller_id,
    created_at,
    last_edited_at,
    capacity,
    fare,
    payment,
    ride_id,
    service_id,
    service_type,
    service_token_transit_use_deeplink,
    pickup,
    dropoff,
    origin,
    destination,
    schedule,
    source,
    note,
    status,
    status_history,
    user_id,
    rider,
    created_by,
    wheelchair,
    bicycle,
    messages,
    actual_pickup,
    actual_dropoff,
    actual_no_show,
    shape,
    intermediate_stops,
    vehicle_id,
    in_advance,
    dispatcher_assigned_vehicle_id,
  }) {
    const pickupObj = pickup || {};
    const dropoffObj = dropoff || {};
    const pickupTime = pickupObj.timestamp ? moment.utc(pickupObj.timestamp) : null;
    const dropoffTime = dropoffObj.timestamp ? moment.utc(dropoffObj.timestamp) : null;
    const originObj = origin || {};
    const destinationObj = destination || {};
    this.rideId = ride_id || null;
    this.agencyId = agency || null;
    this.serviceId = service_id || null;
    this.capacity = capacity || 0;
    this.terminalReason = terminal_reason || '';
    this.fare = fare || null;
    this.payment = payment || null;
    this.wheelchair = wheelchair || false;
    this.bicycle = bicycle || false;
    this.schedule = schedule ? new RideSchedule(schedule) : null;
    this.status = status || rideStatus.PENDING;
    this.statusHistory = _.map(status_history, (item) => ({
      status: item.status,
      timestamp: moment.utc(item.timestamp),
    }));
    this.serviceType = service_type || serviceTypes.ONDEMAND;
    this.serviceTokenTransitUseDeeplink = service_token_transit_use_deeplink || false;
    this.source = source || null;
    this.note = note || null;
    this.createdAt = created_at ? moment.utc(created_at) : null;
    this.lastEditedAt = last_edited_at ? moment.utc(last_edited_at) : null;
    this.userId = user_id || null;
    this.rider = new AgencyUser(rider || {});
    this.createdBy = new AgencyUser(created_by || {});
    this.callerId = caller_id || null;
    this.pickup = {
      address: pickupObj.address || null,
      time: pickupTime,
      position: parsePosition(pickupObj.position),
      priority: pickupObj.priority || 0,
    };
    this.dropoff = {
      address: dropoffObj.address || null,
      time: dropoffTime,
      position: parsePosition(dropoffObj.position),
      priority: dropoffObj.priority || 0,
    };
    this.origin = {
      name: originObj.name || null,
      position: parsePosition(originObj.position),
    };
    this.destination = {
      name: destinationObj.name || null,
      position: parsePosition(destinationObj.position),
    };
    this.actualPickup = actual_pickup ? {
      position: parsePosition(actual_pickup.position),
      capacity: actual_pickup.capacity,
      vehicleId: actual_pickup.vehicle_id,
      driver: new AgencyUser(actual_pickup.driver || {}),
    } : null;
    this.actualDropoff = actual_dropoff ? {
      position: parsePosition(actual_dropoff.position),
      capacity: actual_dropoff.capacity,
      vehicleId: actual_dropoff.vehicle_id,
      driver: new AgencyUser(actual_dropoff.driver || {}),
    } : null;
    this.actualNoShow = actual_no_show ? {
      position: parsePosition(actual_no_show.position),
      capacity: actual_no_show.capacity,
      vehicleId: actual_no_show.vehicle_id,
      driver: new AgencyUser(actual_no_show.driver || {}),
    } : null;
    this.messages = _.map(messages, (message) => ({
      body: message.body,
      key: message.key,
      success: message.success,
      timestamp: moment.utc(message.timestamp),
    }));
    this.shape = shape ? _.map(shape, ({latitude, longitude, timestamp}) => ({
      latitude,
      longitude,
      timestamp: moment.utc(timestamp),
    })) : [];
    this.intermediateStops = intermediate_stops ?
      _.map(intermediate_stops, ({latitude, longitude, timestamp}) => ({
        latitude,
        longitude,
        timestamp: moment.utc(timestamp),
      })) : [];
    this.vehicleId = vehicle_id || null;
    this.inAdvance = in_advance || false;
    this.dispatcher_assigned_vehicle_id = dispatcher_assigned_vehicle_id || null;
  }
  /* eslint-enable camelcase */

  get bounds() {
    const positions = [];
    if (this.pickup && this.pickup.position) positions.push(this.pickup.position);
    if (this.dropoff && this.dropoff.position) positions.push(this.dropoff.position);
    if (this.origin && this.origin.position) positions.push(this.origin.position);
    if (this.destination && this.destination.position) positions.push(this.destination.position);
    return positionBounds(positions);
  }

  get hasPickupOrDropoff() {
    return (this.pickup && this.pickup.position) || (this.dropoff && this.dropoff.position);
  }

  get pickupCurrentlyPrioritized() {
    return !!this.pickup.priority && _.contains([
      rideStatus.PENDING,
      rideStatus.NEEDS_APPROVAL,
    ], this.status);
  }

  get dropoffCurrentlyPrioritized() {
    return !!this.dropoff.priority && this.isActive;
  }

  get preferredVehicleCurrentlyPrioritized() {
    return !this.vehicle && [rideStatus.PENDING, rideStatus.NEEDS_APPROVAL].includes(this.status);
  }

  get currentlyPrioritized() {
    return this.pickupCurrentlyPrioritized || this.dropoffCurrentlyPrioritized;
  }

  get readableRideStatus() {
    return readableRideStatus[this.status];
  }

  get priority() {
    return (this.pickup.priority || this.dropoff.priority);
  }

  get readableRideEvents() {
    return readableRideEvents[this.status];
  }

  get readableRideSource() {
    const src = this.source.toLowerCase().split('_').map(str =>
      str.charAt(0).toUpperCase() + str.slice(1));
    return src.join(' ');
  }

  get pickupAddress() {
    return this.pickup && this.pickup.address || 'Custom Pickup';
  }

  get dropoffAddress() {
    return this.dropoff && this.dropoff.address || 'Custom Drop-off';
  }

  get isActive() {
    return _.contains([
      rideStatus.PENDING,
      rideStatus.NEEDS_APPROVAL,
      rideStatus.IN_PROGRESS,
    ], this.status);
  }

  get isCurrent() {
    return this.pickup &&
      this.pickup.time &&
      this.pickup.time.isBefore(moment().add(15, 'minutes'));
  }

  calculateWait(initialTime) {
    let wait = 0;
    if (this.status === rideStatus.PENDING) {
      wait = initialTime - this.pickup.time;
    } else if (this.status === rideStatus.IN_PROGRESS) {
      const inProgressStatus = _.findWhere(this.statusHistory, {status: rideStatus.IN_PROGRESS});
      wait = inProgressStatus ? initialTime - inProgressStatus.timestamp : 0;
    }
    return wait / 1000; // return in seconds
  }

  get currentWait() {
    return this.calculateWait(moment.utc());
  }

  get actualPickuptime() {
    const pickup = _.findWhere(this.statusHistory, {status: rideStatus.IN_PROGRESS});
    if (pickup) {
      return pickup.timestamp;
    }
    return null;
  }

  get noShowTime() {
    const noShow = _.findWhere(this.statusHistory, {status: rideStatus.NO_SHOW});
    if (noShow) {
      return noShow.timestamp;
    }
    return null;
  }

  clone() {
    return new Ride(this.serialize());
  }

  compare(other) {
    return naturalSort.compare(this.long_name, other.long_name);
  }

  serialize() {
    return omitNull({
      ride_id: this.rideId,
      agency: this.agencyId,
      service_id: this.serviceId,
      capacity: this.capacity,
      terminal_reason: this.terminalReason,
      fare: this.fare,
      payment: this.payment,
      wheelchair: this.wheelchair,
      bicycle: this.bicycle,
      schedule: this.schedule ? this.schedule.serialize() : null,
      status: this.status,
      status_history: _.map(this.statusHistory, (item) => ({
        status: item.status,
        timestamp: item.timestamp.format('Y-MM-DDTHH:mm:ss.SSS[Z]'),
      })),
      service_type: this.serviceType,
      pickup: omitNull({
        address: this.pickup.address,
        position: serializePosition(this.pickup.position),
        timestamp: this.pickup.time ? this.pickup.time.format('Y-MM-DDTHH:mm:ss.SSS[Z]') : null,
        priority: this.pickup.priority,
      }),
      dropoff: omitNull({
        address: this.dropoff.address,
        position: serializePosition(this.dropoff.position),
        timestamp: this.dropoff.time ? this.dropoff.time.format('Y-MM-DDTHH:mm:ss.SSS[Z]') : null,
        priority: this.dropoff.priority,
      }),
      actual_pickup: this.actualPickup ? omitNull({
        position: serializePosition(this.actualPickup.position),
        capacity: this.actualPickup.capacity,
        vehicle_id: this.actualPickup.vehicleId,
        driver: this.actualPickup.driver ? this.actualPickup.driver.serialize() : null,
      }) : null,
      actual_dropoff: this.actualDropoff ? omitNull({
        position: serializePosition(this.actualDropoff.position),
        capacity: this.actualDropoff.capacity,
        vehicle_id: this.actualDropoff.vehicleId,
        driver: this.actualDropoff.driver ? this.actualDropoff.driver.serialize() : null,
      }) : null,
      actual_no_show: this.actualNoShow ? omitNull({
        position: serializePosition(this.actualNoShow.position),
        capacity: this.actualNoShow.capacity,
        vehicle_id: this.actualNoShow.vehicleId,
        driver: this.actualNoShow.driver ? this.actualNoShow.driver.serialize() : null,
      }) : null,
      origin: omitNull({
        name: this.origin.name,
        position: serializePosition(this.origin.position),
      }),
      destination: omitNull({
        name: this.destination.name,
        position: serializePosition(this.destination.position),
      }),
      caller_id: this.callerId,
      user_id: this.userId,
      created_at: this.createdAt ? this.createdAt.format('Y-MM-DDTHH:mm:ss.SSS[Z]') : null,
      last_edited_at: this.lastEditedAt ?
        this.lastEditedAt.format('Y-MM-DDTHH:mm:ss.SSS[Z]') : null,
      rider: this.rider.serialize(),
      created_by: this.createdBy.serialize(),
      source: this.source,
      note: this.note && this.note.length > NOTE_LENGTH ?
        this.note.substring(0, NOTE_LENGTH) : this.note,
      messages: this.messages,
      shape: this.shape ? _.map(this.shape, ({latitude, longitude, timestamp}) => ({
        latitude,
        longitude,
        timestamp: timestamp.format('Y-MM-DDTHH:mm:ss.SSS[Z]'),
      })) : null,
      intermediate_stops: this.intermediateStops ?
        _.map(this.intermediateStops, ({latitude, longitude, timestamp}) => ({
          latitude,
          longitude,
          timestamp: timestamp.format('Y-MM-DDTHH:mm:ss.SSS[Z]'),
        })) : null,
      vehicle_id: this.vehicleId,
    });
  }

  isDifferent(other) {
    return !_.isEqual(this.serialize(), other.serialize());
  }
}



// WEBPACK FOOTER //
// ./src/common/types/Ride.js